import { useEffect, useState } from "react";
import { ActionIcon, Card, Input, Toast } from "@insights/ui";
import { Check, X } from "lucide-react";
import { useParams } from "react-router";
import { Loading, PageHeader } from "../../../shared/components";
import { useWorkspaceCacheStats } from "../../../shared/hooks/useWorkspaceCacheStats";
import { AppShell } from "../../../shared/layout";
import { useCacheRetention } from "../hooks/useCacheRetention";
import "../styles/cache.css";

function formatBytes(bytes: number) {
  if (bytes < 1024 ** 2) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 ** 3) {
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  }

  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

export function CachePage() {
  const { workspaceId } = useParams();
  const cache = useWorkspaceCacheStats(workspaceId);
  const updateRetention = useCacheRetention(workspaceId);
  const storageLimit = cache.data?.current.storage_limit_bytes;
  const configuredRetentionDays = cache.data?.current.retention_days;
  const [retentionDays, setRetentionDays] = useState<number | "">(10);
  const [retentionError, setRetentionError] = useState<string>();
  const canEdit = ["owner", "admin"].includes(cache.data?.role ?? "");
  const retentionIsValid =
    retentionDays === "" ||
    (Number.isInteger(retentionDays) &&
      retentionDays >= 1 &&
      retentionDays <= 3650);
  const retentionIsDirty =
    configuredRetentionDays !== undefined &&
    retentionDays !== (configuredRetentionDays ?? "");

  useEffect(() => {
    if (configuredRetentionDays !== undefined) {
      setRetentionDays(configuredRetentionDays ?? "");
    }
  }, [configuredRetentionDays]);

  async function saveRetention() {
    setRetentionError(undefined);

    try {
      await updateRetention.mutateAsync(
        retentionDays === "" ? null : retentionDays,
      );
    } catch (error) {
      setRetentionError(
        error instanceof Error
          ? error.message
          : "Unable to update cache retention.",
      );
    }
  }

  function cancelRetentionUpdate() {
    setRetentionDays(configuredRetentionDays ?? "");
    setRetentionError(undefined);
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Cache settings & consumption" title="Cache" />
      {cache.isLoading && <Loading />}
      {cache.error && (
        <Card className="cache-error-card">
          <p className="error">Unable to load cache information.</p>
        </Card>
      )}
      {retentionError && (
        <Toast
          onDismiss={() => setRetentionError(undefined)}
          title="Retention not saved"
          tone="danger"
        >
          {retentionError}
        </Toast>
      )}
      {cache.data && (
        <div className="cache-layout">
          <Card
            as="section"
            className="cache-retention-card"
            aria-labelledby="cache-retention-title"
          >
            <div className="cache-retention-heading">
              <div>
                <h2 id="cache-retention-title">Cache retention</h2>
                <p>
                  Choose how many days completed cache artifacts are kept.
                  Leave the field empty to retain them indefinitely.
                </p>
              </div>
              <div className="cache-retention-editor">
                <label htmlFor="cache-retention-days">
                  Retention period in days
                </label>
                <div className="cache-retention-controls">
                  <Input
                    id="cache-retention-days"
                    type="number"
                    min={1}
                    max={3650}
                    step={1}
                    placeholder="Infinite"
                    value={retentionDays}
                    disabled={!canEdit || updateRetention.isPending}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setRetentionDays(value === "" ? "" : Number(value));
                      setRetentionError(undefined);
                    }}
                  />
                  <ActionIcon
                    color="accent"
                    disabled={
                      !canEdit ||
                      !retentionIsDirty ||
                      !retentionIsValid ||
                      updateRetention.isPending
                    }
                    icon={<Check aria-hidden="true" />}
                    label="Save cache retention"
                    onClick={() => void saveRetention()}
                  />
                  <ActionIcon
                    disabled={
                      !canEdit ||
                      !retentionIsDirty ||
                      updateRetention.isPending
                    }
                    icon={<X aria-hidden="true" />}
                    label="Cancel cache retention changes"
                    onClick={cancelRetentionUpdate}
                  />
                </div>
                {!retentionIsValid && (
                  <p className="error" role="alert">
                    Retention must be between 1 and 3650 days.
                  </p>
                )}
              </div>
            </div>
            {!canEdit && (
              <p className="cache-retention-notice">
                Only workspace owners and admins can change cache retention.
              </p>
            )}
          </Card>
          <div className="cache-metrics">
            <Card as="section" className="cache-metric-card">
              <span>Stored cache</span>
              <strong>{formatBytes(cache.data.current.bytes_used)}</strong>
              <p>
                {storageLimit == null
                  ? "No storage limit is configured."
                  : `${formatBytes(storageLimit)} available on the current plan.`}
              </p>
            </Card>
            <Card as="section" className="cache-metric-card">
              <span>Cached objects</span>
              <strong>{cache.data.current.object_count.toLocaleString()}</strong>
              <p>Objects currently retained in the shared cache.</p>
            </Card>
            <Card as="section" className="cache-metric-card">
              <span>Uploaded this month</span>
              <strong>
                {formatBytes(cache.data.current.monthly_uploaded_bytes)}
              </strong>
              <p>Cache data uploaded during the current usage period.</p>
            </Card>
          </div>
          <Card
            as="section"
            className="cache-history-card"
            aria-labelledby="cache-history-title"
          >
            <h2 id="cache-history-title">Cache consumption</h2>
            <p>Stored cache usage over the last 30 recorded snapshots.</p>
            <div
              className="cache-consumption-chart"
              aria-label="Cache usage over time"
            >
              {cache.data.snapshots.slice(-30).map((snapshot) => {
                const limit = storageLimit ?? (snapshot.bytes_used || 1);

                return (
                  <span
                    key={`${snapshot.recorded_at}-${snapshot.bytes_used}`}
                    title={`${new Date(snapshot.recorded_at).toLocaleString()}: ${formatBytes(snapshot.bytes_used)}`}
                    style={{
                      height: `${Math.max(
                        2,
                        Math.min(100, (snapshot.bytes_used / limit) * 100),
                      )}%`,
                    }}
                  />
                );
              })}
            </div>
            {!cache.data.snapshots.length && (
              <p className="muted">No cache history is available yet.</p>
            )}
          </Card>
        </div>
      )}
    </AppShell>
  );
}
